import React from 'react';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { setLoadableErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';

export function createElementWithCheckForInvalidType(
	type: any,
	rest: any,
	name: string | undefined,
): React.ReactNode {
	// https://github.com/facebook/react/blob/8f8b336734d7c807f5aa11b0f31540e63302d789/packages/react-reconciler/src/ReactFiber.js#L557
	const validType =
		// Functional or class component
		typeof type === 'function' ||
		// built-in HTML elements
		typeof type === 'string' ||
		// react special types like fragments
		typeof type === 'symbol' ||
		// other special types like context and memo
		(typeof type === 'object' && type !== null && type.$$typeof);

	if (!validType) {
		let serialized: string = typeof type;
		try {
			if (serialized === 'object') {
				if (type === null) {
					serialized = 'null';
				} else if (typeof Event !== 'undefined' && type instanceof Event) {
					const parts: string[] = [`eventType=${type.type}`];

					// Extract target element info (e.g. the <script> tag that failed to load)
					try {
						const target = type.target as HTMLElement | null;
						if (target) {
							parts.push(`targetTag=${target.tagName || 'unknown'}`);
							if ('src' in target) {
								parts.push(`src=${(target as HTMLScriptElement).src}`);
							}
							if ('href' in target) {
								parts.push(`href=${(target as HTMLLinkElement).href}`);
							}
						}
					} catch {
						parts.push('target=<unreadable>');
					}

					// ErrorEvent-specific fields
					if ('message' in type && (type as ErrorEvent).message) {
						const errorEvent = type as ErrorEvent;
						parts.push(`message=${errorEvent.message}`);
						if (errorEvent.filename) {
							parts.push(
								`filename=${errorEvent.filename}@${errorEvent.lineno ?? ''}:${errorEvent.colno ?? ''}`,
							);
						}
						if (errorEvent.error) {
							parts.push(`error=${String(errorEvent.error)}`);
						}
					}

					serialized = `Event(${parts.join(', ')})`;
				} else {
					serialized = JSON.stringify(type);
				}
			}
		} catch (e) {
			serialized = `Error serializing ${typeof type}: ${e.message}`;
		}

		const error = new Error(
			`Invalid loadable createElement type: ${serialized}` + (name ? ` (loadable: ${name})` : ''),
		);
		setLoadableErrorAttributes(error, { name });
		getMonitoringClient().submitError(error, {
			attribution: 'backbone-loadable',
		});

		return null;
	}
	return React.createElement(type, rest);
}
